/**
 * JS bridge for ElevateHealthPlugin — the custom Swift Capacitor plugin that
 * writes nutrition macros to Apple Health as an HKCorrelation with type .food.
 *
 * On web / Android this module exports no-op stubs so callers don't need
 * platform guards.
 */

let _plugin = null;

async function getPlugin() {
  if (_plugin !== null) return _plugin;
  try {
    const { Capacitor, registerPlugin } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) {
      _plugin = false;
      return false;
    }
    _plugin = registerPlugin("ElevateHealth");
    return _plugin;
  } catch {
    _plugin = false;
    return false;
  }
}

/**
 * Request HealthKit write authorization for nutrition data types.
 * Safe to call alongside requestHealthAuthorization() — iOS shows one prompt.
 */
export async function requestNutritionAuthorization() {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    const { granted } = await plugin.requestAuthorization();
    return granted;
  } catch {
    return false;
  }
}

/**
 * Write a meal's macros to Apple Health as a single food correlation entry.
 *
 * @param {Object} meal
 * @param {string} meal.name        - Meal name shown in Health app
 * @param {number} meal.calories    - kcal
 * @param {number} meal.protein     - grams
 * @param {number} meal.carbs       - grams
 * @param {number} meal.fat         - grams
 * @param {number} [meal.fiber=0]   - grams
 * @param {number} [meal.timestamp] - Unix ms; defaults to now
 */
export async function syncMealToHealth(meal, servings = 1) {
  const plugin = await getPlugin();
  if (!plugin) return;
  if (!meal?.calories || meal.calories <= 0) return;

  const s = servings > 0 ? servings : 1;
  try {
    const startDate = new Date(meal.timestamp || Date.now()).toISOString();
    await plugin.saveMacros({
      name:      meal.name           || "Meal",
      calories:  (meal.calories || 0) * s,
      protein:   (meal.protein  || 0) * s,
      carbs:     (meal.carbs    || 0) * s,
      fat:       (meal.fat      || 0) * s,
      fiber:     (meal.fiber    || 0) * s,
      startDate,
    });
  } catch (e) {
    console.warn("ElevateHealth macro sync failed:", e);
  }
}
